// Lightweight i18n for the vendia admin shell.
//
// Provides a t(key, params?) function that:
// 1. Reads the user's locale from the runtime (user.locale or settings language).
// 2. Falls back to 'en' when the locale isn't available or a key is missing.
// 3. Supports runtime locale switching via setLocale() + a subscribe() hook
//    for React components to re-render on locale change.
//
// Locale dictionaries live in ./en.json, ./fr.json, ./de.json.
// To add a new language: create <lang>.json and add it to the dictionaries map.
//
// Feature modules may ship their own dictionaries as ./modules/<feature>.<lang>.json
// (e.g. modules/pos.fr.json). They are merged on top of the base dictionaries so
// features can add keys without touching the shared files.

import en from './en.json';
import fr from './fr.json';
import de from './de.json';
import { setLangKeyTranslator } from '../api/client';

const moduleDictionaries = import.meta.glob<Record<string, string>>('./modules/*.json', { eager: true, import: 'default' });

export const availableLocales = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' }
] as const;

const dictionaries: Record<string, Record<string, string>> = {
  en: { ...en },
  fr: { ...fr },
  de: { ...de }
};

for (const [path, entries] of Object.entries(moduleDictionaries)) {
  const match = path.match(/\.([a-z]{2})\.json$/);
  const lang = match?.[1];
  if (lang && dictionaries[lang]) Object.assign(dictionaries[lang], entries);
}

// The active locale — initialized from the runtime, overridable via setLocale().
let activeLocale: string | null = null;
const listeners = new Set<() => void>();

function detectLocale(): string {
  try {
    const runtime = (window as unknown as { __VENDIA_REACT__?: { user?: { locale?: string } } }).__VENDIA_REACT__;
    const locale = runtime?.user?.locale;
    if (locale && dictionaries[locale]) return locale;
    const legacyData = (window as unknown as { data?: { user?: { locale?: string } } }).data;
    const legacyLocale = legacyData?.user?.locale;
    if (legacyLocale && dictionaries[legacyLocale]) return legacyLocale;
  } catch {
    // ignore
  }
  return 'en';
}

/** Returns the active locale code, defaulting to 'en'. */
function getLocale(): string {
  if (activeLocale) return activeLocale;
  return detectLocale();
}

/** Override the active locale at runtime. Notifies all subscribed listeners. */
export function setLocale(locale: string): void {
  if (!dictionaries[locale]) return;
  if (activeLocale === locale) return;
  activeLocale = locale;
  // Persist in localStorage so it survives page reloads.
  try { localStorage.setItem('vendia-locale', locale); } catch { /* ignore */ }
  _clearLabelCache();
  listeners.forEach((fn) => fn());
}

/** Initialize from localStorage on first import. */
try {
  const stored = localStorage.getItem('vendia-locale');
  if (stored && dictionaries[stored]) activeLocale = stored;
} catch { /* ignore */ }

/** Subscribe to locale changes. Returns an unsubscribe function. */
export function subscribeLocale(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** React hook: re-renders the component when the locale changes. */
export function useLocale(): string {
  // Importing React here would create a circular dep in some setups,
  // so we use the simpler pattern of syncing to state in the component.
  return getLocale();
}

/**
 * Translate a key to the active locale, with optional {param} interpolation.
 * Falls back to English, then to the key itself if the key is missing.
 *
 * @example
 * t('nav.products')           // → "Produits" (fr)
 * t('common.saveSuccess', { name: 'Item' })  // → "Item enregistré avec succès."
 */
export function t(key: string, params?: Record<string, string | number>): string {
  const locale = getLocale();
  const dict = dictionaries[locale] ?? dictionaries.en;
  let value = dict[key] ?? dictionaries.en[key] ?? key;
  if (params) {
    for (const [param, replacement] of Object.entries(params)) {
      value = value.replace(new RegExp(`\\{${param}\\}`, 'g'), String(replacement));
    }
  }
  return value;
}

export { getLocale };

/** True when the key exists in the active (or English) dictionary. */
export function hasTranslation(key: string): boolean {
  const dict = dictionaries[getLocale()] ?? dictionaries.en;
  return Boolean(dict[key] ?? dictionaries.en[key]);
}

// Legacy { lang_key } error payloads → api.<lang_key> translations.
setLangKeyTranslator((key) => (hasTranslation(`api.${key}`) ? t(`api.${key}`) : null));
export type Locale = 'en' | 'fr' | 'de';

// ---------------------------------------------------------------------------
// tLabel — auto-translate common field/column/section labels.
// Used by CrudResourcePage and ReportViewPage to translate the labels defined
// in resourceConfigs.ts without modifying the config objects themselves.
// It tries field.<label>, label.<label>, status.<label>, then falls back
// to the raw label.
// ---------------------------------------------------------------------------

const LABEL_CACHE = new Map<string, string>();

function labelKeyVariants(label: string): string[] {
  const normalized = label
    .trim()
    .replace(/&/g, ' and ')
    .replace(/[^A-Za-z0-9]+(.)/g, (_match, chr: string) => chr.toUpperCase())
    .replace(/^[A-Z]/, (chr) => chr.toLowerCase());
  const lower = label.trim().toLowerCase();
  return Array.from(new Set([label, normalized, lower]));
}

/**
 * Translate a label string (e.g. "Date", "Grand Total", "Summary") to the
 * active locale by looking it up across the field.*, label.*, and status.*
 * namespaces. Falls back to the raw label if no translation is found.
 *
 * This is intentionally cache-backed because resource configs contain
 * hundreds of labels and the function is called on every render.
 */
export function tLabel(label: string): string {
  if (!label) return label;
  const locale = getLocale();
  const cacheKey = `${locale}:${label}`;
  const cached = LABEL_CACHE.get(cacheKey);
  if (cached !== undefined) return cached;

  const dict = dictionaries[locale] ?? dictionaries.en;
  // Try multiple namespaces — the label could be a field name, a section title,
  // or a status value.
  const variants = labelKeyVariants(label);
  const candidates = variants.flatMap((variant) => [`field.${variant}`, `label.${variant}`, `status.${variant}`]);
  let result: string | undefined;
  for (const key of candidates) {
    if (dict[key]) { result = dict[key]; break; }
    if (dictionaries.en[key]) { result = dictionaries.en[key]; break; }
  }
  const translated = result ?? label;
  LABEL_CACHE.set(cacheKey, translated);
  return translated;
}

/** Clear the label cache (used after setLocale so cached labels refresh). */
export function _clearLabelCache(): void {
  LABEL_CACHE.clear();
}

// ---------------------------------------------------------------------------
// Locale-aware date/time formatting
// ---------------------------------------------------------------------------

const DATE_KEYS = /^(date|.*_date|.*_at|closed_at|created_at|updated_at|start_date|end_date|expiry_date|in_at|out_at)$/;
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
const DATETIME_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;

/** Returns true if the key name looks like a date/timestamp field. */
export function isDateKey(key: string): boolean {
  return DATE_KEYS.test(key);
}

/** Returns true if the value looks like a date or datetime string. */
export function isDateValue(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  return DATE_ONLY_RE.test(value) || DATETIME_RE.test(value);
}

/**
 * Formats a date string (YYYY-MM-DD or ISO timestamp) to a locale-aware
 * short date (e.g. "Jul 4, 2026" in English, "4 juil. 2026" in French).
 * Returns the original value if it can't be parsed.
 */
export function formatDate(value: unknown): string {
  if (value == null || value === '') return '-';
  const str = String(value);
  // Normalize date-only to ISO format for Date parsing
  const normalized = DATE_ONLY_RE.test(str) ? str + 'T00:00:00' : str;
  const d = new Date(normalized);
  if (isNaN(d.getTime())) return str;
  return d.toLocaleDateString(getLocale(), { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Formats a datetime string to a locale-aware short datetime
 * (e.g. "Jul 4, 2026, 2:30 PM" in English, "4 juil. 2026, 14:30" in French).
 */
export function formatDateTime(value: unknown): string {
  if (value == null || value === '') return '-';
  const str = String(value);
  const normalized = DATE_ONLY_RE.test(str) ? str + 'T00:00:00' : str;
  const d = new Date(normalized);
  if (isNaN(d.getTime())) return str;
  return d.toLocaleString(getLocale(), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * Smart formatter: uses formatDateTime for timestamp values, formatDate for
 * date-only values. Detects based on the string shape.
 */
export function formatDateAuto(value: unknown): string {
  if (typeof value === 'string' && DATETIME_RE.test(value)) return formatDateTime(value);
  return formatDate(value);
}
